import React from 'react';
import clsx from 'clsx';

export type ButtonIntent = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  intent?: ButtonIntent;
  size?: ButtonSize;
}

const intentClasses: Record<ButtonIntent, string> = {
  primary:
    'bg-accent text-white border-transparent hover:bg-accent-hover focus-visible:ring-accent',
  secondary:
    'bg-white/5 text-slate-300 border-white/[0.08] hover:bg-white/10 hover:text-slate-50 focus-visible:ring-accent',
  ghost:
    'bg-transparent text-slate-300 border-transparent hover:bg-white/5 hover:text-slate-50 focus-visible:ring-accent',
  danger:
    'bg-danger text-white border-transparent hover:bg-red-600 focus-visible:ring-danger',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1 text-xs',
  md: 'px-3.5 py-2 text-[13px]',
  lg: 'px-4 py-2.5 text-sm',
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ intent = 'primary', size = 'md', className, type, ...props }, ref) => (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={clsx(
        'inline-flex items-center justify-center gap-1.5 rounded-ds-lg border font-medium transition-colors duration-150',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        intentClasses[intent],
        sizeClasses[size],
        className,
      )}
      {...props}
    />
  ),
);

Button.displayName = 'Button';

export default Button;
